import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  db,
  storage 
} from '../../firebase/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    category: '',
    imageUrl: ''
  });
  
  const [categories, setCategories] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // 加载产品数据和分类数据
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);
  
  // 获取分类列表
  const fetchCategories = async () => {
    try {
      const categoriesRef = collection(db, 'categories');
      const q = query(categoriesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const categoriesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesList);
    } catch (err) {
      console.error('获取分类列表失败:', err);
      setError('获取分类列表失败');
    }
  };

  // 获取产品列表
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const productsRef = collection(db, 'products');
      const snapshot = await getDocs(productsRef);
      const productsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsList);
    } catch (err) {
      console.error('获取产品列表失败:', err);
      setError('获取产品列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理表单输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // 处理图片选择
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      
      // 创建预览URL
      const previewUrl = URL.createObjectURL(file);
      setPreviewImage(previewUrl);
    }
  };
  
  // 上传图片到Firebase Storage - 使用admin页面相同的方法
  const uploadImage = async () => {
    if (!imageFile) return null;
    
    try {
      setUploadingImage(true);
      setError(null);
      
      // 显示进度条
      const progressBar = document.querySelector('#uploadProgress .progress-bar');
      if (progressBar) {
        progressBar.style.width = '0%';
        document.getElementById('uploadProgress').classList.remove('d-none');
      }
      
      // 读取文件为base64
      const reader = new FileReader();
      reader.readAsDataURL(imageFile);
      
      // 等待文件读取完成
      const base64Data = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
      
      // 从data:image/jpeg;base64,/9j/4AAQSkZ... 格式中提取base64部分
      const imageData = base64Data.split(',')[1];
      
      // 设置上传URL - 使用相对路径避免CORS问题
      const uploadUrl = '/api/storage/uploadImage';
      
      // 模拟进度
      if (progressBar) progressBar.style.width = '20%';
      
      // 执行上传请求
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ imageData })
      });
      
      // 更新进度
      if (progressBar) progressBar.style.width = '70%';
      
      // 检查状态
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || '上传失败');
      }
      
      // 模拟进度
      if (progressBar) progressBar.style.width = '90%';
      
      // 解析响应
      const responseData = await response.json();
      
      if (responseData.success) {
        // 获取下载URL
        const downloadURL = responseData.imageUrl;
        
        // 显示成功消息
        setSnackbar({
          open: true,
          message: '图片上传成功',
          severity: 'success'
        });
        
        // 添加到已上传图片数组 - 与admin页面保持一致
        setUploadedImages(prev => [...prev, downloadURL]);
        
        // 更新图片预览
        setPreviewImage(downloadURL);
        
        // 更新图片预览部分的DOM
        updateProductImagePreview();
        
        // 更新表单数据
        setFormData(prev => ({ ...prev, imageUrl: downloadURL }));
        
        // 重置文件输入
        setImageFile(null);
        
        // 完成进度条
        if (progressBar) {
          progressBar.style.width = '100%';
          setTimeout(() => {
            if (document.getElementById('uploadProgress')) {
              document.getElementById('uploadProgress').classList.add('d-none');
            }
          }, 500);
        }
        
        return downloadURL;
      } else {
        throw new Error(responseData.message || '上传失败');
      }
    } catch (err) {
      console.error('上传图片失败:', err);
      setError('上传图片失败: ' + err.message);
      setSnackbar({
        open: true,
        message: '上传图片失败: ' + err.message,
        severity: 'error'
      });
      const progressElement = document.getElementById('uploadProgress');
      if (progressElement) {
        progressElement.classList.add('d-none');
      }
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  // 更新图片预览DOM
  const updateProductImagePreview = () => {
    const productImagePreview = document.getElementById('productImagePreview');
    if (!productImagePreview) return;
    
    productImagePreview.innerHTML = '';
    
    uploadedImages.forEach((imageUrl, index) => {
      const imageContainer = document.createElement('div');
      imageContainer.className = 'position-relative';
      imageContainer.style.display = 'inline-block';
      imageContainer.style.margin = '5px';
      
      imageContainer.innerHTML = `
          <img src="${imageUrl}" alt="产品图片" style="width: 100px; height: 100px; object-fit: cover; border-radius: 4px;">
          <button type="button" class="btn btn-sm btn-danger position-absolute" style="top: -10px; right: -10px; border-radius: 50%; padding: 0.2rem 0.5rem;">×</button>
      `;
      
      // 添加删除按钮点击事件
      const deleteBtn = imageContainer.querySelector('button');
      deleteBtn.addEventListener('click', () => {
        const newImages = [...uploadedImages];
        newImages.splice(index, 1);
        setUploadedImages(newImages);
        updateProductImagePreview();
      });
      
      productImagePreview.appendChild(imageContainer);
    });
  };
  
  // 当uploadedImages变化时更新预览
  useEffect(() => {
    updateProductImagePreview();
  }, [uploadedImages]);

  // 打开编辑对话框
  const handleEdit = (product) => {
    setEditProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      stock: product.stock.toString(),
      category: product.categoryId || '',
      imageUrl: product.imageUrl || ''
    });
    setPreviewImage(product.imageUrl || '');
    
    // 设置上传的图片数组
    if (product.images && Array.isArray(product.images)) {
      setUploadedImages(product.images);
    } else if (product.imageUrl) {
      setUploadedImages([product.imageUrl]);
    } else {
      setUploadedImages([]);
    }
    
    setOpenDialog(true);
  };

  // 处理保存 - 完全按照admin页面的方式处理
  const handleSave = async () => {
    try {
      setLoading(true);
      
      // 验证表单必填项
      if (!formData.name || !formData.price || !formData.category) {
        setError('请填写必填项: 产品名称, 价格和分类');
        setLoading(false);
        return;
      }
      
      // 验证是否已上传图片
      if (uploadedImages.length === 0) {
        setError('请先上传产品图片');
        setLoading(false);
        return;
      }
      
      // 使用已上传的图片URL数组
      const productImages = [...uploadedImages];
      const imageUrl = productImages.length > 0 ? productImages[0] : '';
      
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        categoryId: formData.category,
        imageUrl: imageUrl,  // 主图为第一张图片
        images: productImages,  // 所有上传的图片
        status: 'active', // 确保所有产品状态都设为active
        updatedAt: new Date()
      };

      if (editProduct) {
        // 更新现有产品
        await setDoc(doc(db, 'products', editProduct.id), productData, { merge: true });
        // 显示成功提示
        setSnackbar({
          open: true,
          message: '产品更新成功',
          severity: 'success'
        });
      } else {
        // 创建新产品
        const newProductRef = doc(collection(db, 'products'));
        await setDoc(newProductRef, {
          ...productData,
          status: 'active', // 默认状态
          createdAt: new Date()
        });
        // 显示成功提示
        setSnackbar({
          open: true,
          message: '产品创建成功',
          severity: 'success'
        });
      }

      // 清理
      setOpenDialog(false);
      setImageFile(null);
      setPreviewImage('');
      setUploadedImages([]);
      
      // 重新加载产品
      fetchProducts();
    } catch (err) {
      console.error('保存产品失败:', err);
      setError('保存产品失败: ' + err.message);
      setSnackbar({
        open: true,
        message: '保存产品失败: ' + err.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // 处理删除
  const handleDelete = async (productId) => {
    if (window.confirm('确定要删除这个产品吗？')) {
      try {
        await deleteDoc(doc(db, 'products', productId));
        fetchProducts();
      } catch (err) {
        console.error('删除产品失败:', err);
        setError('删除产品失败');
      }
    }
  };

  // 处理snackbar关闭
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Snackbar通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">产品列表</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setEditProduct(null);
            setFormData({
              name: '',
              description: '',
              price: '',
              stock: '',
              category: ''
            });
            setOpenDialog(true);
          }}
        >
          添加产品
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>产品图片</TableCell>
              <TableCell>名称</TableCell>
              <TableCell>描述</TableCell>
              <TableCell>价格</TableCell>
              <TableCell>库存</TableCell>
              <TableCell>分类</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  {product.imageUrl ? (
                    <Box
                      component="img"
                      src={product.imageUrl}
                      alt={product.name}
                      sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1 }}
                    />
                  ) : (
                    <Box
                      sx={{ 
                        width: 60, 
                        height: 60, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: 'grey.200',
                        borderRadius: 1
                      }}
                    >
                      <ImageIcon color="disabled" />
                    </Box>
                  )}
                </TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>{product.description}</TableCell>
                <TableCell>S${product.price}</TableCell>
                <TableCell>{product.stock}</TableCell>
                <TableCell>
                  {categories.find(c => c.id === product.categoryId)?.name || product.categoryId || '-'}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(product)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(product.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editProduct ? '编辑产品' : '添加产品'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* 左侧表单 */}
            <Grid item xs={12} md={7}>
              <TextField
                margin="dense"
                name="name"
                label="产品名称"
                fullWidth
                required
                value={formData.name}
                onChange={handleInputChange}
              />
              <TextField
                margin="dense"
                name="description"
                label="产品描述"
                fullWidth
                multiline
                rows={4}
                value={formData.description}
                onChange={handleInputChange}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    name="price"
                    label="价格"
                    type="number"
                    fullWidth
                    required
                    value={formData.price}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    margin="dense"
                    name="stock"
                    label="库存"
                    type="number"
                    fullWidth
                    required
                    value={formData.stock}
                    onChange={handleInputChange}
                  />
                </Grid>
              </Grid>
              <FormControl fullWidth margin="dense" required>
                <InputLabel id="category-label">产品分类</InputLabel>
                <Select
                  labelId="category-label"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  label="产品分类"
                >
                  <MenuItem value="">
                    <em>请选择分类</em>
                  </MenuItem>
                  {categories.map(category => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.icon} {category.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>选择适合产品的分类</FormHelperText>
              </FormControl>
            </Grid>
            
            {/* 右侧图片上传 */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  border: '1px dashed grey.400',
                  borderRadius: 1,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.50',
                  height: '100%',
                  minHeight: 250
                }}
              >
                {previewImage ? (
                  <Box sx={{ width: '100%', position: 'relative' }}>
                    <Box
                      component="img"
                      src={previewImage}
                      alt="产品预览"
                      sx={{ 
                        width: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        mb: 2
                      }}
                    />
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ mr: 1 }}
                      >
                        更换图片
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleImageChange}
                        />
                      </Button>
                      {imageFile && (
                        <Button 
                          variant="contained" 
                          color="primary"
                          onClick={async () => {
                            const url = await uploadImage();
                            if (url) {
                              setFormData(prev => ({ ...prev, imageUrl: url }));
                              setError(null);
                              setImageFile(null);
                            }
                          }}
                          disabled={!imageFile || uploadingImage}
                          startIcon={uploadingImage ? <CircularProgress size={20} /> : <SaveIcon />}
                          sx={{ mr: 1 }}
                        >
                          {uploadingImage ? '上传中...' : '上传图片'}
                        </Button>
                      )}
                      <Button 
                        variant="outlined" 
                        color="error"
                        onClick={() => {
                          setPreviewImage('');
                          setImageFile(null);
                          setFormData(prev => ({ ...prev, imageUrl: '' }));
                        }}
                      >
                        移除
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      上传产品图片
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                      >
                        选择图片
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleImageChange}
                        />
                      </Button>
                      {imageFile && (
                        <Button 
                          variant="contained" 
                          color="primary"
                          onClick={async () => {
                            const url = await uploadImage();
                            if (url) {
                              setFormData(prev => ({ ...prev, imageUrl: url }));
                              setError(null);
                              setImageFile(null);
                            }
                          }}
                          disabled={uploadingImage}
                          startIcon={uploadingImage ? <CircularProgress size={20} /> : <SaveIcon />}
                        >
                          {uploadingImage ? '上传中...' : '上传图片'}
                        </Button>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        支持JPG, PNG格式, 最大5MB
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenDialog(false);
              setImageFile(null);
              setPreviewImage('');
            }}
          >
            取消
          </Button>
          <Button 
            onClick={handleSave} 
            color="primary" 
            variant="contained"
            disabled={loading || uploadingImage}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductManagement; 